import mixpanel from 'mixpanel-browser';

const API_MIXPANEL_HOST = 'https://api-eu.mixpanel.com';

class Mixpanel {
  private static instance: Mixpanel | null = null;
  private isInitialized = false;

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  static getInstance(): Mixpanel {
    if (Mixpanel.instance == null) {
      Mixpanel.instance = new Mixpanel();
    }
    return Mixpanel.instance;
  }

  identify(userId: string, userProperties?: Record<string, unknown>): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      mixpanel.identify(userId);
      if (userProperties != null) {
        mixpanel.people.set(userProperties);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`❌ [Mixpanel] Failed to identify user:`, error);
    }
  }

  init(): void {
    if (this.isInitialized) {
      return;
    }

    const token = import.meta.env.VITE_MIXPANEL_TOKEN;

    if (token == null || token === '') {
      return;
    }

    try {
      mixpanel.init(token, {
        api_host: API_MIXPANEL_HOST,
        autocapture: false,
        persistence: 'localStorage',
        record_sessions_percent: 0,
        track_pageview: false,
      });

      this.isInitialized = true;

      mixpanel.track('App Initialized', {
        environment: import.meta.env.VITE_DEVELOPMENT,
        timestamp: new Date().toISOString(),
      });

      // eslint-disable-next-line no-console
      console.log('✅ [Mixpanel] Connected successfully (custom events only)');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ [Mixpanel] Initialization failed:', error);
    }
  }

  reset(): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      mixpanel.reset();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ [Mixpanel] Failed to reset:', error);
    }
  }

  track(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      mixpanel.track(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`❌ [Mixpanel] Failed to track event "${eventName}":`, error);
    }
  }
}

export const initMixpanel = () => {
  Mixpanel.getInstance().init();
};

export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  Mixpanel.getInstance().track(eventName, properties);
};

export const identifyUser = (userId: string, userProperties?: Record<string, unknown>) => {
  Mixpanel.getInstance().identify(userId, userProperties);
};

export const resetAnalytics = () => {
  Mixpanel.getInstance().reset();
};
